import { WarningTriangle } from 'iconoir-react'
import moment from 'moment'
import { ReactElement } from 'react'
import { Button } from 'app/components/ui/button'
import { cn } from 'app/lib/utils'
import { FAILURE_REASON_LABEL } from './smsFailedReplies'
import type { UseSmsAgentOpsReturn } from './useSmsAgentOps'

type AiReplyHealthBannerProps = Pick<
  UseSmsAgentOpsReturn,
  `waitingNow` | `waitingCheckedAt` | `lastAiReplyDate` | `showWaiting` | `recentFailures` | `showFailedReplies`
>

/**
 * Tops the tab while patients are waiting on the AI or its replies are failing, and says which
 * kind of trouble it is (plan D33, D37, D39). When the AI has sent nothing since the newest waiting
 * patient texted, replies have stopped: red, an outage. When it is still replying elsewhere, the
 * waits are one-off misses: amber. A failed attempt in the last hour that nothing has answered
 * since adds why (TRK-5908), and shows the banner in red even before anyone has waited 5 minutes.
 * The copy stays plain. Laid out like the app's other page banners (the PMS notices in
 * `AvailabilityViewComponent`): one tinted row, icon, text, action, in the danger and warning
 * theme tokens so dark mode follows.
 */
export function AiReplyHealthBanner({
  waitingNow,
  waitingCheckedAt,
  lastAiReplyDate,
  showWaiting,
  recentFailures,
  showFailedReplies,
}: AiReplyHealthBannerProps): ReactElement | null {
  // Longest wait first, so the last row is the newest.
  const newest = waitingNow?.at(-1)
  const latestFailure = recentFailures[0]
  if (!waitingCheckedAt || (!newest && !latestFailure)) return null

  const lastReplyAt = lastAiReplyDate ? Date.parse(lastAiReplyDate) : undefined
  const isOutage =
    !!latestFailure || !newest || lastReplyAt === undefined || lastReplyAt < Date.parse(newest.textedDate)
  const failedBecause = latestFailure
    ? ` The last attempt failed: ${(latestFailure.reason && FAILURE_REASON_LABEL[latestFailure.reason]) || `Unknown error`}.`
    : ``

  let headline: string
  let detail: string
  if (newest && waitingNow) {
    const patients = `${waitingNow.length} ${waitingNow.length === 1 ? `patient` : `patients`}`
    headline = `No AI responses for ${patients}.`
    // Measured to when the list loaded, like the report's Waited column.
    detail = lastAiReplyDate
      ? `The latest AI response was ${moment(lastAiReplyDate).from(waitingCheckedAt)}.`
      : `There is no AI response on record.`
  } else {
    const texts = `${recentFailures.length} patient ${recentFailures.length === 1 ? `text` : `texts`}`
    headline = `The AI failed to reply to ${texts} in the last hour.`
    detail = `It retries automatically.`
  }

  return (
    <div
      role="alert"
      className={cn(
        `mb-6 flex flex-wrap items-center gap-3 rounded-lg border px-4 py-3`,
        isOutage ? `border-danger-subtle bg-danger-minimal` : `border-warning-subtle bg-warning-minimal`,
      )}
    >
      <WarningTriangle className={cn(`h-5 w-5 shrink-0`, isOutage ? `text-danger-default` : `text-warning-default`)} />
      <p className={cn(`min-w-0 flex-1 text-body-sm`, isOutage ? `text-danger-strong` : `text-warning-strong`)}>
        <span className="text-ui-sm">{headline}</span> {detail}
        {failedBecause}
      </p>
      <Button variant="white" size="xs" onClick={newest ? showWaiting : showFailedReplies}>
        {newest ? `See who is waiting` : `See failed replies`}
      </Button>
    </div>
  )
}
